import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaLock, FaUser, FaKey, FaArrowLeft } from 'react-icons/fa';
import { BeatLoader } from 'react-spinners';
import { Container, Card, Form, Button, Alert } from 'react-bootstrap';
import axios from 'axios';
import '../styles/RecuperarContrasena.css';

const RecuperarContrasena = () => {
  const [step, setStep] = useState(1);
  const [legajo, setLegajo] = useState('');
  const [claveAnterior, setClaveAnterior] = useState('');
  const [nuevaClave, setNuevaClave] = useState('');
  const [confirmarClave, setConfirmarClave] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [intentosFallidos, setIntentosFallidos] = useState(0);
  const navigate = useNavigate();

  const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4004').replace(/\/$/, '');

  const validarCredenciales = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        CdUsuario: legajo.trim(),
        Password: claveAnterior.trim()
      });

      if (response.data.success) {
        setStep(2);
        setSuccess('Credenciales válidas. Ahora puedes cambiar tu contraseña.');
      } else {
        throw new Error('Credenciales inválidas');
      }
    } catch (error) {
      setIntentosFallidos(prev => prev + 1);
      setError('Legajo o contraseña incorrectos');
    } finally {
      setIsLoading(false);
    }
  };

  const cambiarContrasena = async () => {
    if (nuevaClave !== confirmarClave) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (nuevaClave.length < 6) {
      setError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      
      await axios.put(`${API_URL}/api/auth/cambiar-password`, {
        CdUsuario: legajo.trim(),
        PasswordAnterior: claveAnterior.trim(),
        PasswordNuevo: nuevaClave.trim()
      });

      setSuccess('¡Contraseña cambiada exitosamente! Redirigiendo al login...');
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      setError(error.response?.data?.message || 'Error al cambiar la contraseña');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (step === 1) {
      validarCredenciales();
    } else {
      cambiarContrasena();
    }
  };

  return (
    <div className="recuperar-container">
      <Container className="d-flex justify-content-center align-items-center min-vh-100">
        <Card className="recovery-card shadow" style={{width: '100%', maxWidth: '450px'}}>
          <Card.Header className="text-center bg-primary text-white">
            <h4 className="mb-0">
              <FaKey className="me-2" />
              Recuperar Contraseña
            </h4>
          </Card.Header>
          <Card.Body className="p-4">
            {/* Indicador de paso */}
            <div className="text-center mb-4">
              <div className="step-indicator">
                <span className={`step ${step >= 1 ? 'active' : ''}`}>1</span>
                <span className="step-divider"></span>
                <span className={`step ${step >= 2 ? 'active' : ''}`}>2</span>
              </div>
              <p className="text-muted mt-2">
                Paso {step} de 2: {step === 1 ? 'Validar credenciales' : 'Nueva contraseña'}
              </p>
            </div>

            <Form onSubmit={handleSubmit}>
              {step === 1 ? (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FaUser className="me-2" />
                      Legajo de Usuario
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={legajo}
                      onChange={(e) => setLegajo(e.target.value)}
                      placeholder="Ingresa tu legajo"
                      required
                      disabled={isLoading}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FaLock className="me-2" />
                      Contraseña Actual
                    </Form.Label>
                    <Form.Control
                      type="password"
                      value={claveAnterior}
                      onChange={(e) => setClaveAnterior(e.target.value)}
                      placeholder="Ingresa tu contraseña actual"
                      required
                      disabled={isLoading}
                    />
                  </Form.Group>
                </>
              ) : (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FaKey className="me-2" />
                      Nueva Contraseña
                    </Form.Label>
                    <Form.Control
                      type="password"
                      value={nuevaClave}
                      onChange={(e) => setNuevaClave(e.target.value)}
                      placeholder="Ingresa la nueva contraseña"
                      required
                      disabled={isLoading}
                    />
                    <Form.Text className="text-muted">
                      Mínimo 6 caracteres
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FaKey className="me-2" />
                      Confirmar Nueva Contraseña
                    </Form.Label>
                    <Form.Control
                      type="password"
                      value={confirmarClave}
                      onChange={(e) => setConfirmarClave(e.target.value)}
                      placeholder="Confirma la nueva contraseña"
                      required
                      disabled={isLoading}
                    />
                  </Form.Group>
                </>
              )}

              {error && (
                <Alert variant="danger" className="mb-3">
                  {error}
                </Alert>
              )}

              {success && (
                <Alert variant="success" className="mb-3">
                  {success}
                </Alert>
              )}

              <div className="d-grid gap-2">
                <Button 
                  type="submit" 
                  variant="primary" 
                  disabled={isLoading}
                  size="lg"
                >
                  {isLoading ? (
                    <>
                      <BeatLoader color="white" size={8} className="me-2" />
                      Procesando...
                    </>
                  ) : (
                    step === 1 ? 'Validar Credenciales' : 'Cambiar Contraseña'
                  )}
                </Button>
                
                <Button 
                  variant="outline-secondary"
                  onClick={() => navigate('/')}
                  disabled={isLoading}
                >
                  <FaArrowLeft className="me-2" />
                  Volver al Login
                </Button>
              </div>
            </Form>

            {intentosFallidos >= 3 && (
              <Alert variant="warning" className="mt-3">
                <strong>Atención:</strong> Has tenido varios intentos fallidos. 
                Si continúas con problemas, contacta al administrador del sistema.
              </Alert>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default RecuperarContrasena;